import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import shared translations from common (market module)
import marketES from 'common/src/i18n/locales/es/market.json';
import marketEN from 'common/src/i18n/locales/en/market.json';
import marketZH from 'common/src/i18n/locales/zh/market.json';
import dashboardMarketES from 'common/src/i18n/locales/es/dashboard.json';
import dashboardMarketEN from 'common/src/i18n/locales/en/dashboard.json';
import commonMarketES from 'common/src/i18n/locales/es/common.json';
import commonMarketEN from 'common/src/i18n/locales/en/common.json';
import commonMarketZH from 'common/src/i18n/locales/zh/common.json';

// Import app-specific translations
import sidebarES from './es/sidebar.json';
import sidebarEN from './en/sidebar.json';

import dashboardES from './es/dashboard.json';
import dashboardEN from './en/dashboard.json';

import shipmentsES from './es/shipments.json';
import shipmentsEN from './en/shipments.json';

import excelUploadES from './es/excel-upload.json';
import excelUploadEN from './en/excel-upload.json';

import commitmentsES from './es/commitments.json';
import commitmentsEN from './en/commitments.json';

import settlementsES from './es/settlements.json';
import settlementsEN from './en/settlements.json';
import inboxES from './es/inbox.json';
import inboxEN from './en/inbox.json';

import settlementsComparativeES from './es/settlements-comparative.json';
import settlementsComparativeEN from './en/settlements-comparative.json';

import preSettlementsES from './es/pre-settlements.json';
import preSettlementsEN from './en/pre-settlements.json';

import teamES from './es/team.json';
import teamEN from './en/team.json';

import settingsES from './es/settings.json';
import settingsEN from './en/settings.json';

import logbookES from './es/logbook.json';
import logbookEN from './en/logbook.json';

import usdaMarketES from './es/usda-market.json';
import usdaMarketEN from './en/usda-market.json';

import securityES from './es/security.json';
import securityEN from './en/security.json';
import reportsES from './es/reports.json';
import reportsEN from './en/reports.json';
import reportsZH from './zh/reports.json';
import volumeES from './es/volume.json';
import volumeEN from './en/volume.json';
import shipmentCasesES from './es/shipment-cases.json';
import shipmentCasesEN from './en/shipment-cases.json';

const resources = {
  es: {
    sidebar: sidebarES,
    common: { ...commonMarketES },
    dashboard: { ...dashboardES, ...dashboardMarketES },
    market: marketES,
    shipments: shipmentsES,
    excelUpload: excelUploadES,
    commitments: commitmentsES,
    settlements: settlementsES,
    inbox: inboxES,
    settlementsComparative: settlementsComparativeES,
    preSettlements: preSettlementsES,
    team: teamES,
    settings: settingsES,
    logbook: logbookES,
    'usda-market': usdaMarketES,
    security: securityES,
    reports: reportsES,
    volume: volumeES,
    shipmentCases: shipmentCasesES,
  },
  en: {
    sidebar: sidebarEN,
    common: { ...commonMarketEN },
    dashboard: { ...dashboardEN, ...dashboardMarketEN },
    market: marketEN,
    shipments: shipmentsEN,
    excelUpload: excelUploadEN,
    commitments: commitmentsEN,
    settlements: settlementsEN,
    inbox: inboxEN,
    settlementsComparative: settlementsComparativeEN,
    preSettlements: preSettlementsEN,
    team: teamEN,
    settings: settingsEN,
    logbook: logbookEN,
    'usda-market': usdaMarketEN,
    security: securityEN,
    reports: reportsEN,
    volume: volumeEN,
    shipmentCases: shipmentCasesEN,
  },
  zh: {
    sidebar: sidebarEN, // Fallback to English for now
    common: commonMarketZH,
    dashboard: dashboardMarketES, // Fallback to Spanish for now
    market: marketZH,
    shipments: shipmentsEN, // Fallback to English for now
    excelUpload: excelUploadEN, // Fallback to English for now
    commitments: commitmentsEN, // Fallback to English for now
    settlements: settlementsEN, // Fallback to English for now
    inbox: inboxEN,
    settlementsComparative: settlementsComparativeEN, // Fallback to English for now
    preSettlements: preSettlementsEN, // Fallback to English for now
    team: teamEN, // Fallback to English for now
    settings: settingsEN, // Fallback to English for now
    logbook: logbookEN, // Fallback to English for now
    'usda-market': usdaMarketEN, // Fallback to English for now
    security: securityEN, // Fallback to English for now
    reports: reportsZH,
    volume: volumeEN, // Fallback to English for now
  },
};

// Custom language detector to read from Zustand persist storage
const zustandLanguageDetector = {
  type: 'languageDetector' as const,
  async: false,
  detect: () => {
    try {
      const stored = localStorage.getItem('language-storage');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.state?.language || 'es';
      }
    } catch (error) {
      console.error('Error reading language from localStorage:', error);
    }
    return 'es';
  },
  init: () => {},
  cacheUserLanguage: () => {},
};

i18n
  .use(zustandLanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'es',
    defaultNS: 'common',
    ns: ['common', 'sidebar', 'dashboard', 'market', 'shipments', 'excelUpload', 'commitments', 'settlements', 'settlementsComparative', 'preSettlements', 'team', 'settings', 'logbook', 'usda-market', 'security', 'reports', 'volume'],

    interpolation: {
      escapeValue: false, // React already escapes values
    },
  });

export default i18n;
